import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { 
  Home, Waves, Folder, ClipboardCheck, BarChart3, TrendingUp, Globe, HelpCircle, Menu, X, ChevronRight, Package, Shield, Search, ShoppingCart, ArrowRight, Sun, Moon, Settings, CreditCard, Bookmark, ScrollText, Inbox
} from 'lucide-react';
import { useWaveCart } from '../contexts/WaveCartContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBrandProfile } from '../contexts/BrandProfileContext';
import { useAuth } from '../contexts/AuthContext';
import BrandProfileModal from '../components/brand/BrandProfileModal';
import WalletBalanceCard from '../components/WalletBalanceCard';

const navItems = [
  { path: '/home', label: 'Home', icon: Home },
  { path: '/build-wave', label: 'Plan Wave', icon: Waves },
  { path: '/campaigns', label: 'Campaigns', icon: Folder },
  { path: '/templates', label: 'Wave Library', icon: Bookmark },
  { path: '/portfolio', label: 'Product Portfolio', icon: Package },
  { path: '/discover', label: 'Discover Creators', icon: Search },
  { path: '/drafts', label: 'Review Queue', icon: ClipboardCheck },
  { path: '/insights', label: 'Insights & Analytics', icon: BarChart3 },
  { path: '/trending', label: "What's Trending", icon: TrendingUp },
  { path: '/brand-safety', label: 'Brand Safety', icon: Shield },
  { path: '/data-room', label: '1st Party Data', icon: Globe },
  { path: '/contracts', label: 'Engagement Contracts', icon: ScrollText },
  { path: '/billing', label: 'Billing', icon: CreditCard },
  { path: '/support', label: 'Support', icon: HelpCircle },
];

export default function DashboardLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [draftsCount, setDraftsCount] = useState(0);
  const location = useLocation();
  const { count: cartCount, clearCart } = useWaveCart();
  const { theme, toggleTheme, isDark } = useTheme();
  const { brandName, primaryVertical } = useBrandProfile();
  const { user } = useAuth();

  // Live drafts-to-review count for the sidebar red-dot badge.
  React.useEffect(() => {
    if (!user) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    let cancelled = false;
    const load = () => {
      fetch(`${API}/api/brand/drafts/queue-count`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : { count: 0 })
        .then(d => { if (!cancelled) setDraftsCount(Number(d?.count || 0)); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000); // 60s poll
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);
  const displayBrand = user?.brand_name || brandName || 'Brand';
  const subLabel = user?.subscription_tier
    ? `${user.subscription_tier.charAt(0).toUpperCase()}${user.subscription_tier.slice(1)} Plan`
    : 'Free Trial';
  const avatarInitials = (displayBrand.split(/\s+/).map(w => w[0]).join('').slice(0, 2) || 'WV').toUpperCase();

  return (
    <div className="min-h-screen bg-[#181A20] flex" data-testid="dashboard-layout">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/70 z-40 lg:hidden backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[220px] bg-[#181A20] border-r border-[#242730] flex flex-col transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-[#242730]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#00D4FF] to-[#0088AA] flex items-center justify-center">
              <Waves className="h-4 w-4 text-white" />
            </div>
            <span className="text-base font-bold tracking-tight text-white">WAVE</span>
          </div>
          <button className="lg:hidden ml-auto text-gray-400" onClick={() => setMobileOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-[#00D4FF]/10 text-[#00D4FF]'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-[#242730]'
                }`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span>{item.label}</span>
                {item.path === '/drafts' && draftsCount > 0 && (
                  <span data-testid="nav-drafts-badge" className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#F59E0B] text-black text-[9px] font-black">
                    {draftsCount}
                  </span>
                )}
                {isActive && item.path !== '/drafts' && <ChevronRight className="h-3 w-3 ml-auto opacity-50" />}
                {isActive && item.path === '/drafts' && draftsCount === 0 && <ChevronRight className="h-3 w-3 ml-auto opacity-50" />}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-[#242730] p-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#00D4FF] to-[#FF4D94] flex items-center justify-center text-[10px] font-bold text-white">{avatarInitials}</div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-gray-300 truncate">{displayBrand}</p>
              <p className="text-[10px] text-gray-600 truncate">{subLabel}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-14 flex items-center justify-between px-4 lg:px-6 border-b border-[#242730] bg-[#181A20]/80 backdrop-blur-md sticky top-0 z-30">
          <button className="lg:hidden p-2 -ml-2 text-gray-400 hover:text-white" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <WalletBalanceCard compact />
            <button onClick={toggleTheme}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-[#242730]"
              style={isDark ? {} : { background: '#EDF2F7' }}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              data-testid="theme-toggle-btn">
              {isDark ? <Sun className="h-4 w-4 text-gray-400 hover:text-yellow-400 transition-colors" /> : <Moon className="h-4 w-4 text-gray-500 hover:text-[#1A202C] transition-colors" />}
            </button>
            <span className="text-[13px] text-gray-500">Welcome back,</span>
            <span className="text-[13px] font-semibold text-white">{displayBrand}</span>
            {primaryVertical && (
              <span className="text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ background: '#00D4FF15', color: '#00D4FF', border: '1px solid #00D4FF30' }} data-testid="primary-vertical-badge">
                {primaryVertical}
              </span>
            )}
            <button onClick={() => setProfileOpen(true)}
              className="p-1.5 rounded-lg hover:bg-[#242730] transition-all"
              title="Brand Profile" data-testid="brand-profile-btn">
              <Settings className="h-4 w-4 text-gray-500 hover:text-[#00D4FF] transition-colors" />
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00D4FF] to-[#FF4D94] flex items-center justify-center text-[11px] font-bold text-white" data-testid="user-avatar">{avatarInitials}</div>
          </div>
        </header>

        <BrandProfileModal open={profileOpen} onOpenChange={setProfileOpen} />

        {/* Page content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>

        {/* Floating Wave Cart Banner */}
        {cartCount > 0 && (
          <div className="sticky bottom-0 z-40 px-4 py-2.5 bg-[#181A20]/95 backdrop-blur-md border-t border-[#00D4FF]/20" data-testid="wave-cart-banner">
            <div className="flex items-center justify-between max-w-[1400px] mx-auto">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#00D4FF]/15 flex items-center justify-center relative">
                  <ShoppingCart className="h-4 w-4 text-[#00D4FF]" />
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#FF4D94] text-white text-[8px] font-black flex items-center justify-center">{cartCount}</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-white">{cartCount} creator{cartCount !== 1 ? 's' : ''} selected for Wave</p>
                  <p className="text-[9px] text-gray-500">Ready to build your cohort</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={clearCart} className="px-3 py-1.5 rounded-lg text-[10px] text-gray-500 hover:text-white bg-[#242730] border border-[#2E3240] transition-all" data-testid="clear-cart-btn">
                  Clear
                </button>
                <Link to="/build-wave" className="px-4 py-2 rounded-lg bg-[#FF4D94] text-white text-[11px] font-bold flex items-center gap-1.5 hover:brightness-110 transition-all"
                      style={{ boxShadow: '0 0 16px rgba(255,77,148,0.35)' }}
                      data-testid="build-wave-cart-btn">
                  <Waves className="h-3.5 w-3.5" />Build wave with these creators <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
